import { useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL, tokenStore } from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Receipt, Loader2, AlertCircle, ShieldCheck, ExternalLink } from "lucide-react";

const CURRENCY_SYMBOL = { EUR: "€", NOK: "kr" };
const METHOD_LABEL = {
  no: {
    stripe: "Stripe", cash: "Kontant", card_terminal: "Kortterminal",
    vipps: "Vipps", bank_transfer: "Bankoverføring", other: "Annet",
  },
  en: {
    stripe: "Stripe", cash: "Cash", card_terminal: "Card terminal",
    vipps: "Vipps", bank_transfer: "Bank transfer", other: "Other",
  },
};

/**
 * Read-only preview of everything the admin has registered on the booking
 * so far. Customer sees line items + running totals, but is clearly told
 * that the final, numbered receipt is generated after the trip.
 */
export default function CustomerReceiptPreview({ lang = "en", booking }) {
  const [payments, setPayments] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!booking?.id) return;
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/bookings/${booking.id}/payments`).then((r) => r.data).catch(() => []),
      api.get(`/bookings/${booking.id}/receipts`).then((r) => r.data).catch(() => []),
    ]).then(([p, r]) => {
      if (!alive) return;
      setPayments(p || []);
      setReceipts(r || []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [booking?.id]);

  const totals = useMemo(() => {
    const acc = {};
    for (const p of payments) {
      const cur = p.currency;
      const t = acc[cur] || { gross: 0, count: 0 };
      t.gross += p.amount;
      t.count += 1;
      acc[cur] = t;
    }
    return acc;
  }, [payments]);

  const fmt = (amount, cur) =>
    `${amount.toLocaleString(lang === "en" ? "en-GB" : "nb-NO", {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    })} ${CURRENCY_SYMBOL[cur] || cur}`;

  const finalReceipt = receipts[0]; // Only ever one per booking

  const copy = lang === "en" ? {
    title: "Preliminary receipt",
    subtitle:
      "Everything registered on this booking so far. The final, numbered receipt is issued after you have completed the trip and checked out.",
    empty: "No charges have been registered yet. Any add-ons or extra services will show up here.",
    final_title: "Final receipt available",
    final_body: (n) => `Your final receipt (${n}) has been issued. Open it below.`,
    open_pdf: "Open receipt (PDF)",
    total: "Total",
    line: "line",
    lines: "lines",
    heading_lines: "Registered items",
    heading_totals: "Sum so far",
  } : {
    title: "Midlertidig kvittering",
    subtitle:
      "Alt som er registrert på bookingen din så langt. Endelig, nummerert kvittering utstedes etter at du har gjennomført turen og sjekket ut.",
    empty:
      "Ingen kostnader er registrert ennå. Eventuelle ekstra tjenester eller tillegg vil dukke opp her.",
    final_title: "Endelig kvittering er klar",
    final_body: (n) => `Din endelige kvittering (${n}) er utstedt. Åpne den nedenfor.`,
    open_pdf: "Åpne kvittering (PDF)",
    total: "Sum",
    line: "linje",
    lines: "linjer",
    heading_lines: "Registrerte poster",
    heading_totals: "Foreløpig sum",
  };

  if (loading) {
    return (
      <Card className="p-6 flex items-center justify-center gap-2 text-[#A57F39]">
        <Loader2 className="w-4 h-4 animate-spin" /> …
      </Card>
    );
  }

  return (
    <Card className="p-5 sm:p-6" data-testid="customer-receipt-preview">
      <div className="flex items-center gap-2 mb-1">
        <Receipt className="w-5 h-5 text-[#A57F39]" />
        <h2 className="text-lg sm:text-xl font-bold text-[#0C1824]">{copy.title}</h2>
      </div>
      <p className="text-xs sm:text-sm text-[#2B3F4D]">{copy.subtitle}</p>

      {finalReceipt ? (
        <div
          className="mt-4 p-3 sm:p-4 rounded-lg border border-emerald-200 bg-emerald-50 flex items-start gap-3 flex-wrap"
          data-testid="customer-final-receipt-banner"
        >
          <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-emerald-800 text-sm">{copy.final_title}</div>
            <div className="text-xs text-emerald-800/80 mt-0.5 break-words">
              {copy.final_body(finalReceipt.receipt_number)}
            </div>
          </div>
          <Button
            onClick={() => {
              const url = `${BACKEND_URL}/api/receipts/${finalReceipt.id}/pdf?auth=${encodeURIComponent(tokenStore.get())}`;
              window.open(url, "_blank");
            }}
            data-testid="customer-open-receipt-pdf"
            className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
          >
            <ExternalLink className="w-4 h-4 mr-1.5" /> {copy.open_pdf}
          </Button>
        </div>
      ) : (
        <div
          className="mt-4 p-3 sm:p-4 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-3"
          data-testid="customer-preview-banner"
        >
          <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs sm:text-sm text-amber-900 break-words">
            {copy.subtitle}
          </div>
        </div>
      )}

      {payments.length === 0 ? (
        <div className="mt-5 text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-lg">
          {copy.empty}
        </div>
      ) : (
        <>
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A57F39] font-semibold mb-2">
              {copy.heading_lines}
            </div>
            <div className="space-y-2" data-testid="customer-receipt-lines">
              {payments.map((p) => (
                <div
                  key={p.id}
                  data-testid={`customer-receipt-line-${p.id}`}
                  className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-white"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0C1824] text-sm sm:text-base truncate">
                      {p.description}
                    </div>
                    <div className="text-[11px] text-[#2B3F4D] mt-0.5 flex flex-wrap gap-x-1.5">
                      <span>{METHOD_LABEL[lang]?.[p.method] || p.method}</span>
                      <span>·</span>
                      <span>{p.paid_at}</span>
                      {p.vat_rate ? <><span>·</span><span>MVA {p.vat_rate}%</span></> : null}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-[#0C1824] tabular-nums text-sm sm:text-base">
                      {fmt(p.amount, p.currency)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="customer-receipt-totals">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A57F39] font-semibold sm:col-span-2">
              {copy.heading_totals}
            </div>
            {Object.entries(totals).map(([cur, t]) => (
              <div
                key={cur}
                className="rounded-lg bg-[#0C1824] text-white p-3"
                data-testid={`customer-receipt-total-${cur}`}
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#ECC974]">
                  {copy.total} · {cur} · {t.count} {t.count === 1 ? copy.line : copy.lines}
                </div>
                <div className="text-xl font-mono font-bold mt-0.5 tabular-nums">
                  {fmt(t.gross, cur)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!finalReceipt && (
        <div className="mt-4 text-[11px] text-slate-500 italic">
          {lang === "en"
            ? "This preview is provisional and may change until the trip is completed."
            : "Denne oversikten er foreløpig og kan endres inntil turen er gjennomført."}
        </div>
      )}
    </Card>
  );
}
